import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Instagram, Zap, ExternalLink, Users, Search,
  TrendingUp, Clock, CheckCircle, History,
  Upload, MessageSquare, BarChart3, Info
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const fmtDate  = (d) => { try { return d ? format(parseISO(d), 'MMM d, yyyy h:mm a') : '-'; } catch { return '-'; } };
const fmtShort = (d) => { try { return d ? format(parseISO(d), 'MMM d') : '-'; } catch { return '-'; } };

const INTEL_TYPE_STYLES = {
  restaurant_opening: { bg: 'bg-amber-50 border-amber-200',  text: 'text-amber-700',  dot: 'bg-amber-500',  label: 'Restaurant Opening' },
  event:              { bg: 'bg-purple-50 border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500', label: 'Event' },
  real_estate:        { bg: 'bg-teal-50 border-teal-200',    text: 'text-teal-700',   dot: 'bg-teal-500',   label: 'Real Estate' },
  hospitality_trend:  { bg: 'bg-blue-50 border-blue-200',    text: 'text-blue-700',   dot: 'bg-blue-500',   label: 'Hospitality Trend' },
  competitor:         { bg: 'bg-red-50 border-red-200',      text: 'text-red-700',    dot: 'bg-red-500',    label: 'Competitor' },
  opportunity:        { bg: 'bg-green-50 border-green-200',  text: 'text-green-700',  dot: 'bg-green-500',  label: 'Opportunity' },
  seasonal:           { bg: 'bg-sky-50 border-sky-200',      text: 'text-sky-700',    dot: 'bg-sky-500',    label: 'Seasonal' },
  general:            { bg: 'bg-muted border-border',        text: 'text-muted-foreground', dot: 'bg-muted-foreground', label: 'General' },
};

const BUILTIN_ACCOUNTS = [
  'mavericksmontauk','shagwong','bouncebeachmtk','alimentaribeach','thebirdmontauk',
  'southamptonsocialclub','babesagharbor','jonismontauk','hamptonscoffeeco','tatesbakedshop',
  'almondbridgehampton','75main','lunchlobsterroll','harveysmontauk','bespoke.realestate',
  'kdhamptons','hamptonssocial','vuehamptons','hamptonsmag','danspapers','27east',
  'joevanascohamptons','hamptonspoloclub','hamptonclassic','palmadayclub',
  'taukthetauk','shannoninthehamptons','sagharbor','blamptons',
];

function IntelCard({ item, idx }) {
  const style = INTEL_TYPE_STYLES[item.intel_type] || INTEL_TYPE_STYLES.general;
  const engColor = item.engagement === 'high' ? 'text-green-600 bg-green-50' :
                   item.engagement === 'medium' ? 'text-amber-600 bg-amber-50' :
                   'text-muted-foreground bg-muted';
  return (
    <Card className="h-full flex flex-col hover:shadow-md transition-shadow" data-testid={`ig-intel-card-${idx}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <a href={`https://www.instagram.com/${(item.account||'').replace('@','')}/`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs font-bold text-pink-600 hover:underline">
                <Instagram size={11} />{item.account}
              </a>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${style.bg} ${style.text}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${style.dot}`} />
                {style.label}
              </span>
              {item.engagement && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${engColor}`}>{item.engagement}</span>
              )}
            </div>
            <CardTitle className="text-sm leading-snug" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem' }}>
              {item.headline}
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 flex flex-col flex-1 gap-2">
        {item.detail && <p className="text-sm text-muted-foreground leading-relaxed flex-1">{item.detail}</p>}
        {item.action && (
          <div className="flex items-start gap-2 p-2.5 rounded-lg bg-primary/5 border border-primary/15">
            <Zap size={11} className="text-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-primary font-medium leading-relaxed">{item.action}</p>
          </div>
        )}
        {item.post_url && item.post_url !== 'N/A' && (
          <a href={item.post_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
            <ExternalLink size={10} /> View post
          </a>
        )}
      </CardContent>
    </Card>
  );
}

export default function InstagramIntel() {
  const [latest,      setLatest]      = useState(null);
  const [history,     setHistory]     = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [running,     setRunning]     = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [filterType,  setFilterType]  = useState('All');
  const [following,   setFollowing]   = useState(null);
  const [pasteText,   setPasteText]   = useState('');
  const [importing,   setImporting]   = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [latestRes, histRes, followingRes] = await Promise.all([
        api.get('/api/intel/instagram/latest'),
        api.get('/api/intel/instagram?limit=10'),
        api.get('/api/intel/instagram/following'),
      ]);
      const lat = latestRes.data && Object.keys(latestRes.data).length > 0 ? latestRes.data : null;
      setLatest(lat);
      setSelected(lat);
      setHistory(histRes.data || []);
      setFollowing(followingRes.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const runBot = async () => {
    setRunning(true);
    try {
      const res = await api.post('/api/intel/instagram/run');
      toast.success(res.data.message);
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const r = await api.get('/api/intel/instagram/latest');
          const data = r.data && Object.keys(r.data).length > 0 ? r.data : null;
          if (data && (!latest || data.run_time !== latest?.run_time)) {
            clearInterval(poll);
            setLatest(data); setSelected(data);
            setHistory(prev => [data, ...prev.filter(h => h.id !== data.id)]);
            toast.success(`Done — ${data.intel_items?.length || 0} intel items! SMS sent.`);
            setRunning(false);
          }
        } catch {}
        if (attempts >= 25) { clearInterval(poll); setRunning(false); }
      }, 6000);
    } catch {
      toast.error('Failed to start bot');
      setRunning(false);
    }
  };

  const importFollowing = async () => {
    const lines = pasteText.split('\n').map(l => l.trim()).filter(Boolean);
    const usernames = lines.flatMap(l =>
      l.replace(/https?:\/\/www\.instagram\.com\//g, '')
       .replace(/\//g, '').replace(/@/g, '')
       .split(/[,\s]+/).map(u => u.trim())
       .filter(u => u && u.length > 0 && u.length < 40)
    );
    if (!usernames.length) { toast.error('No valid usernames found'); return; }
    setImporting(true);
    try {
      const res = await api.post('/api/intel/instagram/import-following', { usernames, source: 'manual_paste' });
      toast.success(res.data.message);
      setPasteText('');
      const r = await api.get('/api/intel/instagram/following');
      setFollowing(r.data);
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  const display         = selected || latest;
  const items           = display?.intel_items || [];
  const filteredItems   = filterType === 'All' ? items : items.filter(i => i.intel_type === filterType);
  const typeCounts      = items.reduce((acc, i) => { const t = i.intel_type||'general'; acc[t]=(acc[t]||0)+1; return acc; }, {});
  const isRealScrape    = display?.method === 'mobile_api_scrape';
  const totalFollowing  = following?.count || BUILTIN_ACCOUNTS.length;
  const accountList     = following?.usernames || BUILTIN_ACCOUNTS;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Instagram size={26} className="text-pink-500" />
            Instagram Intel
          </h1>
          <p className="page-subtitle">
            Scrapes public posts from <span className="font-semibold text-foreground">{totalFollowing.toLocaleString()} accounts</span> via
            Instagram mobile API · no login · SMS via RingCentral
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowHistory(!showHistory)} className="gap-1.5">
            <History size={13} /> History ({history.length})
          </Button>
          <Button onClick={runBot} disabled={running}
            className="bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700"
            data-testid="ig-run-btn">
            {running
              ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Scraping...</span>
              : <span className="flex items-center gap-2"><Search size={14} /> Run Now</span>}
          </Button>
        </div>
      </div>

      {/* Status banner */}
      <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border ${isRealScrape ? 'bg-green-50 border-green-200' : 'bg-pink-50 border-pink-200'}`}>
        <Instagram size={14} className={isRealScrape ? 'text-green-600' : 'text-pink-500'} />
        <span className={`text-xs font-semibold ${isRealScrape ? 'text-green-700' : 'text-pink-700'}`}>
          {isRealScrape ? 'Real post data — scraped via Instagram mobile API' : 'Perplexity web search mode'}
        </span>
        <span className="text-xs text-muted-foreground">No account login · safe from bot detection · RingCentral SMS on completion</span>
        <span className={`ml-auto flex items-center gap-1.5 text-xs font-medium ${isRealScrape ? 'text-green-600' : 'text-pink-600'}`}>
          <span className={`w-2 h-2 rounded-full ${isRealScrape ? 'bg-green-500' : 'bg-pink-500'}`} />
          {isRealScrape ? 'Live data' : 'Web search'}
        </span>
      </div>

      {/* History */}
      {showHistory && history.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><History size={13} />Past Runs</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1.5">
              {history.map((run, i) => (
                <button key={i} onClick={() => { setSelected(run); setShowHistory(false); }}
                  className={`w-full flex items-center justify-between p-2.5 rounded-lg border text-left transition-colors ${selected?.id === run.id ? 'border-pink-300 bg-pink-50' : 'border-border bg-card hover:bg-muted'}`}>
                  <div className="flex items-center gap-3">
                    <Instagram size={13} className="text-pink-500 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium">{run.method === 'mobile_api_scrape' ? 'Real Scrape' : 'Web Search'} — {fmtShort(run.run_time || run.created_at)}</div>
                      <div className="text-xs text-muted-foreground">{run.accounts_scanned} scraped · {run.intel_items?.length || 0} items</div>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{fmtDate(run.run_time || run.created_at)}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="intel">
        <TabsList className="mb-4">
          <TabsTrigger value="intel" className="flex items-center gap-1.5"><TrendingUp size={13} />Intel Feed</TabsTrigger>
          <TabsTrigger value="accounts" className="flex items-center gap-1.5"><Users size={13} />Accounts ({totalFollowing.toLocaleString()})</TabsTrigger>
          <TabsTrigger value="import" className="flex items-center gap-1.5"><Upload size={13} />Import Following</TabsTrigger>
        </TabsList>

        {/* INTEL FEED */}
        <TabsContent value="intel">
          {running && !display ? (
            <Card><CardContent className="py-16 text-center space-y-3">
              <div className="w-10 h-10 rounded-full border-[3px] border-pink-500 border-t-transparent animate-spin mx-auto" />
              <p className="text-sm font-medium">Scanning ALL {totalFollowing.toLocaleString()} accounts via Perplexity...</p>
              <p className="text-xs text-muted-foreground">Running {Math.ceil(totalFollowing / 40)} search queries — takes ~10-15 min. SMS sent when complete.</p>
            </CardContent></Card>
          ) : !display ? (
            <Card><CardContent className="py-20 text-center space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center mx-auto">
                <Instagram size={32} className="text-pink-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>No Instagram Intel yet</h3>
                <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                  Click <strong>Run Now</strong> to scrape real posts from {totalFollowing.toLocaleString()} public accounts. Results are texted to your phone.
                </p>
              </div>
              <Button onClick={runBot} disabled={running} className="bg-gradient-to-r from-pink-500 to-purple-600 text-white">
                <Search size={14} className="mr-1.5" /> Run First Scan
              </Button>
            </CardContent></Card>
          ) : (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="p-4 bg-gradient-to-br from-pink-50 to-purple-50 border-pink-200">
                  <div className="flex items-center gap-2 mb-1"><BarChart3 size={14} className="text-pink-500" /></div>
                  <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{display.accounts_scanned || 0}</div>
                  <div className="text-xs text-muted-foreground">Accounts Scraped</div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-1"><TrendingUp size={14} className="text-primary" /></div>
                  <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{display.posts_analyzed || 0}</div>
                  <div className="text-xs text-muted-foreground">Posts Analysed</div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-1"><Zap size={14} className="text-amber-500" /></div>
                  <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{items.length}</div>
                  <div className="text-xs text-muted-foreground">Intel Items</div>
                </Card>
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-1"><MessageSquare size={14} className="text-teal-600" /></div>
                  <div className="text-sm font-bold text-teal-600">SMS Sent</div>
                  <div className="text-xs text-muted-foreground">via RingCentral</div>
                </Card>
              </div>

              {/* Filter chips */}
              {Object.keys(typeCounts).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(typeCounts).sort(([,a],[,b]) => b-a).map(([type, count]) => {
                    const s = INTEL_TYPE_STYLES[type] || INTEL_TYPE_STYLES.general;
                    return (
                      <button key={type} onClick={() => setFilterType(filterType === type ? 'All' : type)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filterType === type ? `${s.bg} ${s.text} ring-1 ring-inset ring-current` : 'bg-card border-border text-muted-foreground hover:bg-muted'}`}>
                        <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                        {s.label} <span className="font-bold">{count}</span>
                      </button>
                    );
                  })}
                  {filterType !== 'All' && (
                    <button onClick={() => setFilterType('All')} className="px-3 py-1.5 rounded-full text-xs text-muted-foreground border border-dashed border-border">Clear</button>
                  )}
                </div>
              )}

              {/* Intel cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredItems.map((item, i) => <IntelCard key={i} item={item} idx={i} />)}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ACCOUNTS */}
        <TabsContent value="accounts">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }} className="text-base">
                  Monitored Accounts ({totalFollowing.toLocaleString()})
                </CardTitle>
                {following?.source === 'manual_paste' && (
                  <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">Your following list</Badge>
                )}
              </div>
              {following?.updated_at && <p className="text-xs text-muted-foreground">Updated: {fmtDate(following.updated_at)}</p>}
          <p className="text-xs text-muted-foreground">
                Bot scans ALL {totalFollowing.toLocaleString()} accounts every run (~10-15 min). All 1,281 accounts covered each time.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {accountList.slice(0, 120).map(acc => (
                  <a key={acc} href={`https://www.instagram.com/${acc}/`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted hover:bg-pink-50 border border-border hover:border-pink-200 text-xs text-muted-foreground hover:text-pink-700 transition-colors">
                    <Instagram size={9} />@{acc}
                  </a>
                ))}
                {totalFollowing > 120 && (
                  <span className="flex items-center px-3 py-1 rounded-full bg-muted border border-border text-xs text-muted-foreground">
                    +{totalFollowing - 120} more
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* IMPORT */}
        <TabsContent value="import">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>Import Your Following List</CardTitle>
                <p className="text-sm text-muted-foreground">Paste Instagram usernames (one per line, comma-separated, or full URLs).</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea value={pasteText} onChange={e => setPasteText(e.target.value)}
                  placeholder={"mavericksmontauk\nshagwong\nbouncebeachmtk\n\nOr paste URLs:\nhttps://www.instagram.com/mavericksmontauk/"}
                  rows={10} className="font-mono text-xs" data-testid="ig-paste-input" />
                <Button onClick={importFollowing} disabled={importing || !pasteText.trim()} className="w-full bg-primary" data-testid="ig-import-btn">
                  {importing
                    ? <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />Importing...</span>
                    : <span className="flex items-center gap-2"><Upload size={14} />Import {pasteText.split('\n').filter(l => l.trim()).length} accounts</span>}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>How to get your following list</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <div>
                  <p className="font-semibold text-foreground mb-1">Option A — Instagram Data Export (full list)</p>
                  <ol className="space-y-1 list-decimal list-inside text-xs">
                    <li>Instagram → Settings → Your Activity</li>
                    <li>Download Your Information → Connections → Following</li>
                    <li>Open <strong>following.json</strong>, copy all username values</li>
                    <li>Paste above</li>
                  </ol>
                </div>
                <Separator />
                <div>
                  <p className="font-semibold text-foreground mb-1">Option B — Manual copy from app</p>
                  <ol className="space-y-1 list-decimal list-inside text-xs">
                    <li>Open @hamptonsxp profile → tap Following</li>
                    <li>Copy usernames and paste above</li>
                  </ol>
                </div>
                <Separator />
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-start gap-2">
                    <Info size={13} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700">
                      Already monitoring <strong>{totalFollowing} accounts</strong>. Importing your full following list lets the bot cover all 1,200+ accounts, rotating ~60/day.
                    </p>
                  </div>
                </div>
                {following?.count > BUILTIN_ACCOUNTS.length && (
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2">
                    <CheckCircle size={13} className="text-green-600" />
                    <p className="text-xs text-green-700 font-medium">{following.count} accounts imported from your following list</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
